import { useEffect, useRef, useState } from 'react';

/** Ширина вмісту зони в один ряд — незалежно від того, чи вона зараз переноситься. */
function rowWidth(el: Element | null): number {
  if (!el) return 0;
  const kids = [...el.children] as HTMLElement[];
  const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
  return kids.reduce((w, k) => w + k.getBoundingClientRect().width, 0) + gap * Math.max(0, kids.length - 1);
}

/**
 * Панель таблиці тримає календар по центру: ліва й права зони однакової
 * ширини. Скільки кнопок праворуч, залежить від сторінки (у нарядах їх майже
 * вдвічі більше), тож межу ширини вікна не вгадати медіазапитом. Міряємо самі:
 * якщо бокова зона не влазить у свою половину, панель стає двома рядами.
 */
export function useToolbarFit<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [stacked, setStacked] = useState(false);
  useEffect(() => {
    const bar = ref.current;
    if (!bar) return;
    const check = (): void => {
      const cs = getComputedStyle(bar);
      const inner = bar.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const gap = parseFloat(cs.columnGap) || 0;
      const mid = bar.querySelector('.toolbar-mid')?.getBoundingClientRect().width ?? 0;
      const side = (inner - mid - 2 * gap) / 2;
      const need = Math.max(rowWidth(bar.querySelector('.toolbar-left')), rowWidth(bar.querySelector('.toolbar-right')));
      setStacked(need > side + 0.5);
    };
    const ro = new ResizeObserver(check);
    ro.observe(bar);
    const mo = new MutationObserver(check);
    mo.observe(bar, { childList: true, subtree: true });
    check();
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, []);
  return { ref, stacked };
}
